import { lazy, Suspense, ReactNode, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@/components/theme-provider'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from '@/features/auth/components/AuthProvider'
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
import { AdminRoute } from '@/features/auth/components/AdminRoute'
import { AdminOrInstructorRoute } from '@/features/auth/components/AdminOrInstructorRoute'
import { PageLoader } from '@/components/PageLoader'
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary'

/**
 * Wraps lazy-loaded components with Suspense.
 * Use this for all lazy() components to show a spinner only for that route.
 */
function L({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

// Public pages.
const HomePage = lazy(() => import('@/features/home/HomePage'))
const CatalogPage = lazy(() => import('@/features/courses/pages/CatalogPage'))
const ClassHubPage = lazy(() => import('@/features/courses/pages/ClassHubPage'))
const LessonPage = lazy(() => import('@/features/courses/pages/LessonPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const MoveLibraryPage = lazy(() => import('@/features/moves/pages/MoveLibraryPage'))
const MoveDetailPage = lazy(() => import('@/features/moves/pages/MoveDetailPage'))
const VideosFeedPage = lazy(() => import('@/features/videos/pages/VideosFeedPage'))
const InstructorProfilePage = lazy(() => import('@/features/instructors/pages/InstructorProfilePage'))
const AboutPage = lazy(() => import('@/features/about/AboutPage'))
const TermsPage = lazy(() => import('@/features/legal/pages/TermsPage'))
const PrivacyPage = lazy(() => import('@/features/legal/pages/PrivacyPage'))
const RefundPage = lazy(() => import('@/features/legal/pages/RefundPage'))
const MyClassesPage = lazy(() => import('@/features/courses/pages/MyClassesPage'))
const VideoPlayerPage = lazy(() => import('@/features/videos/pages/VideoPlayerPage'))
const InstructorRegisterPage = lazy(() => import('@/features/instructors/pages/InstructorRegisterPage'))
const TutorialPlayerPage = lazy(() => import('@/features/tutorials/pages/TutorialPlayerPage'))
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'))
const NotFoundPage = lazy(() => import('@/features/errors/NotFoundPage'))

// Sequences pages - lazy load (heavy: dnd-kit) — admin-only for now
const SequenceBuilder = lazy(() => import('@/features/sequences/SequenceBuilder'))
const MySequencesPage = lazy(() => import('@/features/sequences/MySequencesPage'))

// Admin-only pages (no v2 equivalents — they still live in MainLayout chrome)
const CourseAdminPage = lazy(() => import('@/features/courses/pages/CourseAdminPage').then(m => ({ default: m.CourseAdminPage })))
const OrderHistoryPage = lazy(() => import('@/features/orders/pages/OrderHistoryPage').then(m => ({ default: m.OrderHistoryPage })))
const OrderManagementPage = lazy(() => import('@/features/admin/pages/OrderManagementPage').then(m => ({ default: m.OrderManagementPage })))
const InstructorAdminPage = lazy(() => import('@/features/instructors/pages/InstructorAdminPage').then(m => ({ default: m.InstructorAdminPage })))
const InstructorDashboardPage = lazy(() => import('@/features/instructors/pages/InstructorDashboardPage').then(m => ({ default: m.InstructorDashboardPage })))
const VideoAdminDashboard = lazy(() => import('@/features/videos/pages/VideoAdminDashboard').then(m => ({ default: m.VideoAdminDashboard })))
const VideoCreatePage = lazy(() => import('@/features/videos/pages/VideoCreatePage').then(m => ({ default: m.VideoCreatePage })))
const InstructorVideoCreatePage = lazy(() => import('@/features/videos/pages/InstructorVideoCreatePage').then(m => ({ default: m.InstructorVideoCreatePage })))
const VideoProcessingPage = lazy(() => import('@/features/videos/pages/VideoProcessingPage').then(m => ({ default: m.VideoProcessingPage })))
const VideoEditorPage = lazy(() => import('@/features/videos/pages/VideoEditorPage').then(m => ({ default: m.VideoEditorPage })))
const VideoSettingsPage = lazy(() => import('@/features/videos/pages/VideoSettingsPage').then(m => ({ default: m.VideoSettingsPage })))
const TutorialEditorPage = lazy(() => import('@/features/tutorials/pages/TutorialEditorPage').then(m => ({ default: m.TutorialEditorPage })))
const AdminLayout = lazy(() => import('@/features/admin/AdminLayout').then(m => ({ default: m.AdminLayout })))

const queryClient = new QueryClient()

const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/reset-password']

function AppRoutes() {
  const { isLoading } = useAuth()
  const location = useLocation()

  // Track the last non-auth page so LoginPage can redirect back after login.
  useEffect(() => {
    if (!AUTH_PAGES.some(p => location.pathname.startsWith(p))) {
      sessionStorage.setItem('lastVisitedPath', location.pathname + location.search)
    }
  }, [location.pathname, location.search])

  if (isLoading) {
    return <PageLoader />
  }

  return (
    <ChunkErrorBoundary>
      <Routes>
        {/* Public — home + auth */}
        <Route path="/" element={<L><HomePage /></L>} />
        <Route path="/login" element={<L><LoginPage /></L>} />
        <Route path="/register" element={<L><RegisterPage /></L>} />
        <Route path="/forgot-password" element={<L><ForgotPasswordPage /></L>} />
        <Route path="/reset-password" element={<L><ResetPasswordPage /></L>} />

        {/* Public — legal/about */}
        <Route path="/about" element={<L><AboutPage /></L>} />
        <Route path="/terms" element={<L><TermsPage /></L>} />
        <Route path="/privacy" element={<L><PrivacyPage /></L>} />
        <Route path="/refund" element={<L><RefundPage /></L>} />

        {/* Public — courses */}
        <Route path="/courses" element={<L><CatalogPage /></L>} />
        <Route path="/courses/enrolled" element={<L><MyClassesPage /></L>} />
        <Route path="/courses/:id" element={<L><ClassHubPage /></L>} />
        <Route path="/courses/:id/learn/:lessonId" element={<L><LessonPage /></L>} />

        {/* Public — instructors */}
        <Route path="/instructors/register" element={<L><InstructorRegisterPage /></L>} />
        <Route path="/instructors/:id" element={<L><InstructorProfilePage /></L>} />

        {/* Public — video + tutorial player (direct links stay public) */}
        <Route path="/videos/:id" element={<L><VideoPlayerPage /></L>} />
        <Route path="/tutorials/:vid" element={<L><TutorialPlayerPage /></L>} />

        {/* Logged-in dashboard (placeholder content for now) */}
        <Route path="/dashboard" element={<L><DashboardPage /></L>} />

        {/* Admin-gated: moves, sequences, videos listing — kept behind AdminRoute
            while we pare down the public feature set */}
        <Route path="/moves" element={<AdminRoute><L><MoveLibraryPage /></L></AdminRoute>} />
        <Route path="/moves/:id" element={<AdminRoute><L><MoveDetailPage /></L></AdminRoute>} />
        <Route path="/videos" element={<AdminRoute><L><VideosFeedPage /></L></AdminRoute>} />
        <Route path="/sequences" element={<AdminRoute><L><MySequencesPage /></L></AdminRoute>} />
        <Route path="/sequences/create" element={<AdminRoute><L><SequenceBuilder /></L></AdminRoute>} />
        <Route path="/sequences/:id" element={<AdminRoute><L><SequenceBuilder /></L></AdminRoute>} />

        {/* User pages requiring auth */}
        <Route path="/orders" element={
          <ProtectedRoute>
            <L><OrderHistoryPage /></L>
          </ProtectedRoute>
        } />
        <Route path="/instructors/me" element={
          <ProtectedRoute>
            <L><InstructorDashboardPage /></L>
          </ProtectedRoute>
        } />
        <Route path="/instructors/videos/create" element={
          <ProtectedRoute>
            <L><InstructorVideoCreatePage /></L>
          </ProtectedRoute>
        } />

        {/* Admin */}
        <Route path="/admin" element={
          <AdminRoute>
            <L><AdminLayout /></L>
          </AdminRoute>
        } />
        <Route path="/admin/courses" element={
          <AdminRoute>
            <L><CourseAdminPage /></L>
          </AdminRoute>
        } />
        <Route path="/admin/instructors" element={
          <AdminRoute>
            <L><InstructorAdminPage /></L>
          </AdminRoute>
        } />
        <Route path="/admin/orders" element={
          <AdminRoute>
            <L><OrderManagementPage /></L>
          </AdminRoute>
        } />

        {/* Video admin */}
        <Route path="/admin/videos" element={
          <AdminRoute>
            <L><VideoAdminDashboard /></L>
          </AdminRoute>
        } />
        <Route path="/admin/videos/create" element={
          <AdminOrInstructorRoute>
            <L><VideoCreatePage /></L>
          </AdminOrInstructorRoute>
        } />
        <Route path="/admin/videos/:vid/processing" element={
          <AdminOrInstructorRoute>
            <L><VideoProcessingPage /></L>
          </AdminOrInstructorRoute>
        } />
        <Route path="/admin/videos/:id/edit" element={
          <AdminOrInstructorRoute>
            <L><VideoEditorPage /></L>
          </AdminOrInstructorRoute>
        } />
        <Route path="/admin/videos/:id/settings" element={
          <AdminOrInstructorRoute>
            <L><VideoSettingsPage /></L>
          </AdminOrInstructorRoute>
        } />

        {/* Tutorial editor (admin) */}
        <Route path="/admin/tutorials/:vid/edit" element={
          <AdminOrInstructorRoute>
            <L><TutorialEditorPage /></L>
          </AdminOrInstructorRoute>
        } />

        {/* Catch all */}
        <Route path="*" element={<L><NotFoundPage /></L>} />
      </Routes>
    </ChunkErrorBoundary>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="ui-theme">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="bottom-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
